/**
 * Authentication Store (Zustand)
 * 
 * Manages authentication state including user, tokens, role, and tenant_id.
 * ID Token is stored in localStorage for API Gateway authentication.
 * Refresh token is also stored for session renewal.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User } from '@/types';

/**
 * Authentication state
 */
interface AuthState {
  // User data
  user: User | null;
  
  // ID Token for API Gateway (Cognito Authorizer uses this)
  accessToken: string | null;
  
  // Refresh token for renewing sessions
  refreshToken: string | null;
  
  // Token expiration timestamp
  tokenExpiresAt: number | null;
  
  // Authentication status
  isAuthenticated: boolean;
  isLoading: boolean;
  
  // Actions
  setAuth: (user: User, idToken: string, refreshToken: string, expiresIn: number) => void;
  clearAuth: () => void;
  setUser: (user: User) => void;
  setLoading: (isLoading: boolean) => void;
  isTokenExpired: () => boolean;
  shouldRefreshToken: () => boolean;
}

/**
 * Authentication store with persistence
 * 
 * Provides centralized authentication state management.
 * ID Token and Refresh Token are persisted in localStorage.
 * The ID Token is used for API Gateway Cognito Authorizer authentication.
 */
export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      accessToken: null,
      refreshToken: null,
      tokenExpiresAt: null,
      isAuthenticated: false,
      isLoading: true,

      /**
       * Set authentication data after successful login
       * 
       * @param user - User object
       * @param idToken - JWT ID token (used for API Gateway authentication)
       * @param refreshToken - Refresh token for renewing sessions
       * @param expiresIn - Token expiration time in seconds
       */
      setAuth: (user, idToken, refreshToken, expiresIn) => {
        const tokenExpiresAt = Date.now() + expiresIn * 1000;
        
        set({
          user,
          accessToken: idToken, // Store idToken as accessToken for API calls
          refreshToken,
          tokenExpiresAt,
          isAuthenticated: true,
          isLoading: false,
        });
      },

      /**
       * Clear authentication data on logout
       */
      clearAuth: () => {
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          tokenExpiresAt: null,
          isAuthenticated: false,
          isLoading: false,
        });
      },

      /**
       * Update user data without changing tokens
       * 
       * @param user - Updated user object
       */
      setUser: (user) => {
        set({ user });
      },

      /**
       * Set loading state
       * 
       * @param isLoading - Loading status
       */
      setLoading: (isLoading) => {
        set({ isLoading });
      },

      /**
       * Check if access token is expired
       * 
       * @returns true if token is expired or missing
       */
      isTokenExpired: () => {
        const { tokenExpiresAt } = get();
        if (!tokenExpiresAt) return true;
        return Date.now() >= tokenExpiresAt;
      },

      /**
       * Check if token should be refreshed
       * Refresh if token expires in less than 5 minutes
       * 
       * @returns true if token should be refreshed
       */
      shouldRefreshToken: () => {
        const { tokenExpiresAt } = get();
        if (!tokenExpiresAt) return false;
        
        const fiveMinutes = 5 * 60 * 1000;
        return Date.now() >= tokenExpiresAt - fiveMinutes;
      },
    }),
    {
      name: 'auth-storage', // localStorage key
      partialize: (state) => ({
        // Only persist these fields
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        tokenExpiresAt: state.tokenExpiresAt,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);

/**
 * Selectors for common auth state queries
 */
export const authSelectors = {
  /**
   * Get current user
   */
  user: (state: AuthState) => state.user,

  /**
   * Get user role
   */
  role: (state: AuthState) => state.user?.rol,

  /**
   * Get tenant ID
   */
  tenantId: (state: AuthState) => state.user?.id_tenant,

  /**
   * Get access token
   */
  accessToken: (state: AuthState) => state.accessToken,

  /**
   * Check if authenticated
   */
  isAuthenticated: (state: AuthState) => state.isAuthenticated,

  /**
   * Check if loading
   */
  isLoading: (state: AuthState) => state.isLoading,

  /**
   * Check if user has specific role
   */
  hasRole: (state: AuthState, role: string) => state.user?.rol === role,

  /**
   * Check if user has any of the specified roles
   */
  hasAnyRole: (state: AuthState, roles: string[]) => 
    state.user?.rol ? roles.includes(state.user.rol) : false,

  /**
   * Check if user is in specific Cognito group
   */
  hasGroup: (state: AuthState, group: string) => 
    state.user?.groups?.includes(group) ?? false,
};
